// Research notes — add a fourth and fifth note here; the page renders whatever is listed.
// Body blocks: { type: "p", text } · { type: "h", text } · { type: "list", items: [{ title, body }] }
//              { type: "contrast", columns: [head, head], rows: [[left, right]] } · { type: "close", text }
export const RESEARCH_NOTES = [
  {
    slug: "data-governance-debt",
    year: "2026",
    title: "The first job in enterprise AI adoption is not the model. It is data governance debt resolution and systems insulation.",
    summary: "Until the data is governed and the systems are insulated, every adoption step compounds the risk.",
    body: [
      {
        type: "p",
        text: "Most adoption plans start with a model. Which one, from which lab, at what price. The questions are reasonable. They are also the wrong place to start.",
      },
      {
        type: "p",
        text: "A model does nothing on its own. It acts on the data it is shown and through the systems it is connected to. If the data is unclassified and the systems are open, the model inherits every weakness already in the estate, and acts on it at machine speed.",
      },
      { type: "h", text: "What data governance debt is" },
      {
        type: "p",
        text: "Every organisation carries it. Data governance debt is the gap between the rules an organisation says apply to its data and the rules that are actually enforced. It builds quietly:",
      },
      {
        type: "list",
        items: [
          { title: "Unclassified data.", body: "Nobody can say which records are confidential, which are personal, which are public." },
          { title: "Duplicated data.", body: "The same customer, contract or formula sits in five systems, with five versions of the truth." },
          { title: "Unowned data.", body: "No one is accountable for a dataset, so no one can grant or refuse access to it." },
          { title: "Rules on paper.", body: "A policy exists. Nothing in the system enforces it." },
        ],
      },
      {
        type: "p",
        text: "Before AI, the debt was tolerated. People were the control. A person knew not to paste the customer file into a public tool, or found out afterwards. AI removes that pause. A model will read whatever it is given and carry it wherever it is connected.",
      },
      { type: "h", text: "Why insulation comes before the model" },
      {
        type: "p",
        text: "Insulation is controls you own, in place before harm. Applied to adoption, it means four things are true before the first model is switched on:",
      },
      {
        type: "list",
        items: [
          { title: "The data is classified,", body: "so the system knows what is confidential before the model does." },
          { title: "Access is scoped,", body: "so the model sees the data its mandate requires and nothing else." },
          { title: "The systems it can touch are named,", body: "so it cannot reach past them." },
          { title: "Every action is logged,", body: "so the organisation can see what the model did and why." },
        ],
      },
      {
        type: "p",
        text: "With these in place, the choice of model becomes what it should be: a procurement decision, reversible, made on performance and price.",
      },
      { type: "h", text: "The compounding problem" },
      {
        type: "p",
        text: "Adoption is not a single step. It is a sequence: one team, then a department, then the enterprise; one use case, then ten. If the data is not governed at step one, each step inherits the exposure and adds its own. The cost of fixing the debt rises with every model connected to it. The cheapest moment to resolve data governance debt is before the first pilot, not after the tenth.",
      },
      { type: "h", text: "What this means for the executive" },
      {
        type: "contrast",
        columns: ["Ask first", "Ask later"],
        rows: [
          ["Which data is confidential, and does the system know?", "Which model should we use?"],
          ["Who owns each dataset the AI will touch?", "Which maker?"],
          ["What can the AI reach, and what can it never reach?", "How many licences?"],
          ["What is logged, and who reads it?", "What does it cost per seat?"],
        ],
      },
      {
        type: "close",
        text: "Resolve the debt. Insulate the systems. Then choose the model. In that order, every adoption step reduces the risk instead of compounding it.",
      },
    ],
  },
  {
    slug: "too-many-pilots",
    year: "2026",
    title: "Too many pilots means too many unpredictable risks.",
    summary:
      "Scattered experiments multiply exposure faster than anyone can track. AI must be owned by the executives and the board, not left to pilots: clear goals, a clear why, and clear integration of AI skills into the operating model.",
    body: [
      {
        type: "p",
        text: "Walk through most enterprises today and you will find AI already in use. Not one deployment, but dozens. A team in finance testing a model on forecasts. Marketing drafting with another. An engineer connecting a third to a live database to see what happens. Each is small. Each is sensible on its own. Together, they are an unmanaged risk.",
      },
      { type: "h", text: "How pilots multiply exposure" },
      {
        type: "p",
        text: "A pilot is designed to learn, not to be governed. That is its value and its danger. Each pilot opens its own path:",
      },
      {
        type: "list",
        items: [
          { title: "Its own data.", body: "Whatever the team had to hand, often copied out of the system of record to make the test easier." },
          { title: "Its own tool.", body: "Chosen by the team, under terms nobody in legal has read." },
          { title: "Its own permissions.", body: "Granted quickly, rarely withdrawn." },
          { title: "Its own outputs.", body: "Flowing back into decisions with no mark that AI produced them." },
        ],
      },
      {
        type: "p",
        text: "Five pilots create five of each. Fifty pilots create fifty. No one holds the list. Exposure grows in proportion to the number of experiments, not the value they produce.",
      },
      { type: "h", text: "Why the board, not the pilot" },
      {
        type: "p",
        text: "The argument for pilots is that they let the organisation learn before it commits. The argument against is that the organisation has already committed: its data is already in the tools. The question is no longer whether to adopt AI but whether adoption is directed.",
      },
      {
        type: "p",
        text: "Direction cannot come from the pilot. The team running it has a local goal and local permissions. Only the executives and the board hold the three things adoption at scale requires:",
      },
      {
        type: "list",
        items: [
          { title: "Clear goals.", body: "Where AI will work first, measured how, and when the organisation moves on." },
          { title: "A clear why.", body: "The reason, stated once, that every team can test its own experiment against." },
          { title: "Clear integration.", body: "AI skills written into roles, into the operating model and into who is accountable for what the AI does." },
        ],
      },
      { type: "p", text: "Without these, pilots do not add up to a strategy. They add up to a surface." },
      { type: "h", text: "From pilots to a programme" },
      {
        type: "contrast",
        columns: ["Pilots", "A programme"],
        rows: [
          ["Many tools, chosen locally", "One environment, approved centrally"],
          ["Data copied to the tool", "Data stays where it is governed"],
          ["Permissions granted and forgotten", "Permissions scoped and reviewed"],
          ["Outputs enter decisions unmarked", "Outputs traced to the model that produced them"],
          ["Learning stays with the team", "Learning is written into the operating model"],
        ],
      },
      {
        type: "p",
        text: "The pilots do not have to stop. They have to move inside a box the organisation controls, where the same gates apply to every experiment, and where the list of what is running is held in one place.",
      },
      {
        type: "close",
        text: "Experiments are how an organisation learns. Unowned experiments are how it loses control. Put the executives and the board in charge of the why, the goals and the integration, and the pilots become a programme.",
      },
    ],
  },
  {
    slug: "not-software",
    year: "2026",
    title: "Don't treat AI like you treat software.",
    summary:
      "Treat AI adoption the way you would treat the recruitment of a high-value resource into your organisation: vetted, given a mandate, held to account.",
    body: [
      {
        type: "p",
        text: "Software does what it is told. It follows the rule it was given, every time, until someone changes the rule. That predictability is why organisations have a settled way of buying it: specify, procure, install, train, support.",
      },
      {
        type: "p",
        text: "AI does not behave like that. It acts on judgement. It generalises from what it has seen. It can be confidently wrong. It can do more than it was asked. Bought like software, it will be governed like software, and the controls will not fit.",
      },
      { type: "h", text: "Where the software model breaks" },
      {
        type: "list",
        items: [
          { title: "Scope.", body: "Software has a feature list. AI has a capability it will apply to anything in reach." },
          { title: "Error.", body: "Software fails visibly, with a message. AI fails fluently, with an answer that reads as true." },
          { title: "Drift.", body: "Software stays the same until updated. AI models change with versions, with data and with use." },
          { title: "Access.", body: "Software is given a database. AI is given a mandate, and will interpret it." },
        ],
      },
      {
        type: "p",
        text: "Each of these is a people problem before it is a technology problem. Organisations already know how to manage people with judgement. They recruit.",
      },
      { type: "h", text: "The recruitment model" },
      {
        type: "p",
        text: "Consider how an organisation brings in an accomplished PhD. It does not fear the hire, even though the person knows more than the people managing them. It is not afraid because the structures exist:",
      },
      {
        type: "list",
        items: [
          { title: "Vetted.", body: "References checked, credentials verified, before any access is granted." },
          { title: "Given a mandate.", body: "A role, a scope, the systems and data the role requires, and nothing more." },
          { title: "Inducted.", body: "Taught the rules of the house: what is confidential, who decides." },
          { title: "Supervised.", body: "A manager who reviews the work and can see what was done." },
          { title: "Held to account.", body: "Performance measured, authority withdrawn if trust is broken." },
        ],
      },
      { type: "p", text: "AI is the same. Each stage has a direct equivalent:" },
      {
        type: "contrast",
        columns: ["Recruiting a person", "Adopting AI"],
        rows: [
          ["Reference and credential checks", "Model evaluated on your data, on your use cases, before it is connected"],
          ["Role description and reporting line", "A written mandate: what it may do, touch and see"],
          ["Induction into house rules", "Gates that enforce those rules before each action"],
          ["Manager reviews the work", "Every action logged and visible in one place"],
          ["Performance review and consequences", "Measured outcomes; access withdrawn when a gate is breached"],
        ],
      },
      { type: "h", text: "What changes when you adopt this way" },
      {
        type: "p",
        text: "The conversation moves from procurement to leadership. The question is no longer which tool but what mandate, who supervises, what happens when it is wrong. These are executive questions. They are the questions Lead.ai exists to prepare executives and boards to answer, and the structures Aky exists to enforce.",
      },
      {
        type: "close",
        text: "You are not afraid to recruit an accomplished PhD, because you have the structures to direct, contain and get value from that person. Build the same structures for AI, and the fear goes with them.",
      },
    ],
  },
];

export const findNote = (slug) => RESEARCH_NOTES.find((n) => n.slug === slug);
